import React, { useEffect, useState } from 'react'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { searchSchools, type SchoolOption } from '@/lib/api'

interface SchoolPickerProps {
  id?: string
  value: SchoolOption | null
  onChange: (school: SchoolOption) => void
  invalid?: boolean
  className?: string
  'aria-describedby'?: string
}

const SUPPORT_EMAIL = 'support@athios.ai'

/**
 * Searchable school list for signup and the profile quest. The server ranks the matches (names,
 * short names like "umd", school email domains), so the list is shown as it comes back.
 */
const SchoolPicker: React.FC<SchoolPickerProps> = ({
  id,
  value,
  onChange,
  invalid,
  className,
  'aria-describedby': describedBy,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SchoolOption[]>([])
  const [status, setStatus] = useState<'idle' | 'searching' | 'done' | 'error'>('idle')

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      setStatus('idle')
      return
    }
    // Earlier results stay up until the new ones arrive, so the list doesn't flicker
    setStatus('searching')
    let cancelled = false
    const t = setTimeout(async () => {
      try {
        const schools = await searchSchools(q)
        if (cancelled) return
        setResults(schools ?? [])
        setStatus('done')
      } catch {
        if (!cancelled) setStatus('error')
      }
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [query])

  const select = (school: SchoolOption) => {
    onChange(school)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-11 w-full justify-between border-foreground/10 bg-card font-normal text-foreground hover:bg-foreground/10 hover:text-foreground',
            invalid && 'border-red-500/60',
            className,
          )}
        >
          <span className={cn('truncate', value ? 'text-foreground' : 'text-gray-600')}>
            {value ? value.name : 'Search for your school…'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-600" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="border-foreground/10 bg-card p-0"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command className="bg-transparent" shouldFilter={false}>
          <CommandInput
            placeholder="School name or short name"
            value={query}
            onValueChange={setQuery}
            className="text-base text-foreground placeholder:text-gray-600 sm:text-sm"
          />
          <CommandList>
            {results.length > 0 && (
              <CommandGroup>
                {results.map((school) => (
                  <CommandItem
                    key={school.id}
                    value={String(school.id)}
                    onSelect={() => select(school)}
                    className="cursor-pointer text-foreground data-[selected=true]:bg-foreground/10 data-[selected=true]:text-foreground"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value?.id === school.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">{school.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          <div className="border-t border-foreground/10 px-3 py-2.5 text-xs text-gray-600">
            {status === 'idle' && (
              <p>Type at least 2 letters: your school&apos;s name, or a short name like UMD.</p>
            )}
            {status === 'searching' && results.length === 0 && (
              <p className="flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
              </p>
            )}
            {status === 'error' && (
              <p className="text-red-600">Couldn&apos;t search schools. Check your connection.</p>
            )}
            {(status === 'done' || (status === 'searching' && results.length > 0)) && (
              <p>
                {results.length === 0 ? 'No school matches that. ' : "Don't see it? "}
                Try another spelling, or email{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('My school is not listed')}`}
                  className="text-[#4d7c0f] underline"
                >
                  {SUPPORT_EMAIL}
                </a>{' '}
                and we&apos;ll add it.
              </p>
            )}
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default SchoolPicker
